import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  RotateCcw,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from "pdfjs-dist";
// '?url' liefert nur den Pfad als String — der große pdfjs-Rumpf wird weiter
// unten dynamisch nachgeladen.
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

/**
 * Zeigt ein PDF in unserer eigenen Oberfläche: die Seiten werden mit pdf.js
 * selbst auf ein <canvas> gezeichnet.
 *
 * Der Unterschied zu einem <iframe> auf die Datei: dort übernimmt das
 * PDF-Programm des Browsers die Anzeige — dunkelgraue Fläche, fremde
 * Werkzeugleiste, auf iOS und in der App oft gar nichts. Hier bestimmen wir
 * Aussehen und Bedienung selbst.
 *
 * Achtung: pdf.js lädt die Datei per fetch. Das geht nur bei Quellen, die
 * CORS erlauben — unser Supabase Storage tut das, Google Drive nicht.
 * Für Drive-Links bleibt deshalb der alte iframe-Weg (siehe PdfViewer).
 */

const ZOOM_MIN = 0.6;
const ZOOM_MAX = 2.0;
const ZOOM_SCHRITT = 0.15;

interface PdfCanvasViewerProps {
  url: string;
  /** 'width' füllt die Breite und scrollt, 'page' zeigt die ganze Seite */
  fitMode?: "width" | "page";
  /** Knöpfe, die rechts in die Leiste wandern (Download, Neuer Tab …) */
  toolbarExtras?: ReactNode;
  /**
   * Der Aufrufer muss eine Höhe vorgeben — meist `h-full` in einem Container
   * mit eigener Höhe. Ohne definierte Höhe wächst die Seite über ihren Rahmen
   * hinaus und wird abgeschnitten, statt zu scrollen.
   */
  className?: string;
  /** meldet Scheitern nach außen, damit der Aufrufer zurückfallen kann */
  onError?: () => void;
  /**
   * Seite, auf der das Dokument aufgeht (ab 1; zu groß = letzte Seite). Ohne
   * Angabe Seite 1 wie bisher. Gilt beim Laden, danach blättert man frei.
   */
  startSeite?: number;
  /** Meldet die aufgeschlagene Seite (ab 1), z. B. für „Tonart ändern“ im Kursplayer. Wahlfrei. */
  onSeite?: (seite: number) => void;
}

export function PdfCanvasViewer({
  url,
  fitMode = "width",
  toolbarExtras,
  className,
  onError,
  startSeite,
  onSeite,
}: PdfCanvasViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const flaecheRef = useRef<HTMLDivElement | null>(null);
  // Dokument und Renderlauf gehören zusammen — beide über Refs, damit ein
  // alter Lauf nicht in eine neue Ansicht zeichnet.
  const dokumentRef = useRef<PDFDocumentProxy | null>(null);
  // destroy() hängt am Ladevorgang, nicht am Dokument.
  const ladeAufgabeRef = useRef<PDFDocumentLoadingTask | null>(null);
  const laufRef = useRef(0);
  // Zuletzt gezeichnete Fläche — damit der ResizeObserver nicht ohne echte
  // Größenänderung neu zeichnet.
  const letzteGroesseRef = useRef({ breite: 0, hoehe: 0 });
  // Der laufende Zeichenvorgang muss abgebrochen werden, bevor ein neuer auf
  // dasselbe <canvas> zeichnet — pdf.js weist sonst ab ("Cannot use the same
  // canvas during multiple render() operations") und die Anzeige bräche ab.
  const zeichenAufgabeRef = useRef<{ cancel: () => void; promise: Promise<void> } | null>(null);

  const [seite, setSeite] = useState(1);
  const [seitenZahl, setSeitenZahl] = useState(0);
  // Über eine Ref, damit ein neuer Wert das Dokument nicht neu lädt.
  const startSeiteRef = useRef(startSeite);
  startSeiteRef.current = startSeite;
  const onSeiteRef = useRef(onSeite);
  onSeiteRef.current = onSeite;
  useEffect(() => onSeiteRef.current?.(seite), [seite]);
  const [zoom, setZoom] = useState(1);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState(false);
  const [versuch, setVersuch] = useState(0);

  // --- Dokument laden -----------------------------------------------------
  useEffect(() => {
    let abgebrochen = false;
    setLaedt(true);
    setFehler(false);
    setSeite(1);

    const laden = async () => {
      try {
        // pdfjs-dist ist groß (~1 MB) — nur laden, wenn wirklich ein PDF kommt.
        const pdfjs = await import("pdfjs-dist");
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

        const ladeAufgabe = pdfjs.getDocument({ url });
        ladeAufgabeRef.current = ladeAufgabe;
        const doc = await ladeAufgabe.promise;
        if (abgebrochen) {
          void ladeAufgabe.destroy();
          return;
        }
        dokumentRef.current = doc;
        setSeitenZahl(doc.numPages);
        const start = Math.floor(Number(startSeiteRef.current) || 1);
        setSeite(Math.min(Math.max(1, start), doc.numPages));
        setLaedt(false);
      } catch (e) {
        if (abgebrochen) return;
        console.error("PDF konnte nicht geladen werden:", e);
        setFehler(true);
        setLaedt(false);
        onError?.();
      }
    };

    void laden();
    return () => {
      abgebrochen = true;
      void ladeAufgabeRef.current?.destroy();
      ladeAufgabeRef.current = null;
      dokumentRef.current = null;
    };
    // onError absichtlich nicht in den Abhängigkeiten: die Funktion wechselt
    // bei jedem Render des Aufrufers und würde das Dokument neu laden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, versuch]);

  // --- Aktuelle Seite zeichnen -------------------------------------------
  const zeichne = useCallback(async () => {
    const doc = dokumentRef.current;
    const canvas = canvasRef.current;
    const flaeche = flaecheRef.current;
    if (!doc || !canvas || !flaeche) return;

    const lauf = ++laufRef.current;
    // Vorherigen Lauf abbrechen und abwarten — erst danach darf die Größe des
    // Canvas verändert werden.
    const alteAufgabe = zeichenAufgabeRef.current;
    if (alteAufgabe) {
      alteAufgabe.cancel();
      try {
        await alteAufgabe.promise;
      } catch {
        // Abbruch ist der Normalfall, kein Fehler.
      }
      if (zeichenAufgabeRef.current === alteAufgabe) {
        zeichenAufgabeRef.current = null;
      }
    }
    if (lauf !== laufRef.current) return;
    try {
      const pdfSeite = await doc.getPage(seite);
      if (lauf !== laufRef.current) return;

      const basis = pdfSeite.getViewport({ scale: 1 });
      // Innenabstand der Fläche abziehen, sonst entsteht ein Querbalken.
      const breite = Math.max(1, flaeche.clientWidth - 32);
      const hoehe = Math.max(1, flaeche.clientHeight - 32);
      const passScale =
        fitMode === "page"
          ? Math.min(breite / basis.width, hoehe / basis.height)
          : breite / basis.width;
      const viewport = pdfSeite.getViewport({ scale: passScale * zoom });
      letzteGroesseRef.current = { breite, hoehe };

      // Ohne das Gerätepixel-Verhältnis sieht die Seite auf Retina unscharf aus.
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      const aufgabe = pdfSeite.render({
        canvas,
        viewport,
        transform: dpr === 1 ? undefined : [dpr, 0, 0, dpr, 0, 0],
      });
      zeichenAufgabeRef.current = aufgabe;
      await aufgabe.promise;
      if (zeichenAufgabeRef.current === aufgabe) {
        zeichenAufgabeRef.current = null;
      }
    } catch (e) {
      // Ein abgebrochener Lauf ist gewollt und darf keinen Fehler zeigen.
      if (e instanceof Error && e.name === "RenderingCancelledException") return;
      if (lauf !== laufRef.current) return;
      console.error("PDF-Seite konnte nicht gezeichnet werden:", e);
      // Bewusst KEIN onError: das Dokument liegt vor, nur diese Seite streikt.
      // Der Aufrufer würde sonst dauerhaft auf den Browser-Viewer zurückfallen.
      setFehler(true);
    }
  }, [seite, zoom, fitMode]);

  useEffect(() => {
    if (laedt || fehler || seitenZahl === 0) return;
    void zeichne();
  }, [zeichne, laedt, fehler, seitenZahl]);

  // Bei Größenänderung neu zeichnen, sonst bleibt die Seite in alter Breite.
  useEffect(() => {
    const flaeche = flaecheRef.current;
    if (!flaeche || typeof ResizeObserver === "undefined") return;
    let timer: ReturnType<typeof setTimeout>;
    const beobachter = new ResizeObserver(() => {
      const breite = Math.max(1, flaeche.clientWidth - 32);
      const hoehe = Math.max(1, flaeche.clientHeight - 32);
      const alt = letzteGroesseRef.current;
      const breiteGleich = Math.abs(breite - alt.breite) < 2;
      const hoeheGleich = Math.abs(hoehe - alt.hoehe) < 2;
      // Bei 'width' zählt nur die Breite, bei 'page' auch die Höhe.
      if (breiteGleich && (fitMode !== "page" || hoeheGleich)) return;
      clearTimeout(timer);
      timer = setTimeout(() => void zeichne(), 150);
    });
    beobachter.observe(flaeche);
    return () => {
      clearTimeout(timer);
      beobachter.disconnect();
    };
  }, [zeichne, fitMode]);

  const zurueck = () => setSeite((s) => Math.max(1, s - 1));
  const vor = () => setSeite((s) => Math.min(seitenZahl || 1, s + 1));

  if (fehler) {
    return (
      <div
        className={cn(
          "flex min-h-[240px] flex-col items-center justify-center gap-3 rounded-lg bg-muted p-6 text-center",
          className
        )}
      >
        <p className="text-sm text-muted-foreground">
          Das Dokument konnte nicht angezeigt werden.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setFehler(false);
              setVersuch((v) => v + 1);
            }}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Erneut versuchen
          </Button>
          {toolbarExtras}
        </div>
      </div>
    );
  }

  return (
    // min-h-0 steht bewusst HINTER className: ein Flex-Kind hat sonst
    // min-height:auto, schrumpft nicht unter seinen Inhalt und der
    // Scrollbereich wächst aus dem Rahmen heraus. twMerge würde die Klasse
    // sonst durch eine min-h-Angabe des Aufrufers ersetzen.
    <div className={cn("flex h-full flex-col bg-muted", className, "min-h-0")}>
      {/* Eigene Bedienleiste — ersetzt die des Browsers */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b bg-background/95 px-3 py-2 text-sm sm:gap-3">
        <div className="flex items-center gap-1">
          {seitenZahl > 1 && (
            <>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={zurueck}
                disabled={seite <= 1}
                aria-label="Vorherige Seite"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {/* tabular-nums: sonst springt die Leiste bei jedem Seitenwechsel.
                  Auf schmalen Geräten kurz als „1 / 4", damit die Leiste
                  einzeilig bleibt. */}
              <span className="px-1 text-center text-xs tabular-nums text-muted-foreground sm:min-w-[6.5rem]">
                <span className="hidden sm:inline">Seite </span>
                {seite}
                <span className="hidden sm:inline"> von </span>
                <span className="sm:hidden"> / </span>
                {seitenZahl}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={vor}
                disabled={seite >= seitenZahl}
                aria-label="Nächste Seite"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>

        {/* Zoom und Zusatzknöpfe bleiben als eine Gruppe rechts zusammen */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_SCHRITT))}
            disabled={zoom <= ZOOM_MIN}
            aria-label="Verkleinern"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="w-12 text-center text-xs tabular-nums text-muted-foreground">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_SCHRITT))}
            disabled={zoom >= ZOOM_MAX}
            aria-label="Vergrößern"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          </div>

          {toolbarExtras && (
            <>
              <Separator orientation="vertical" className="h-4" />
              {toolbarExtras}
            </>
          )}
        </div>
      </div>

      {/* min-h-0 flex-1 ist Pflicht: sonst holt sich die Fläche ihre Höhe vom
          Inhalt statt vom Rahmen, und fitMode="page" rechnet sich klein. */}
      <div
        ref={flaecheRef}
        className="relative min-h-0 flex-1 overflow-auto outline-none"
        tabIndex={0}
      >
        {laedt && (
          <div className="absolute inset-0 z-10 flex items-center justify-center gap-3 bg-muted">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">
              PDF wird geladen …
            </span>
          </div>
        )}
        <div className="flex justify-center p-4">
          <canvas
            ref={canvasRef}
            className="block max-w-full rounded-md bg-background shadow-sm"
          />
        </div>
      </div>
    </div>
  );
}
